<div class="wrapper" tabindex="-1" data-testid="drawing-set-page-panel">
  <!-- W46 item 7: a failed save, as the error message (Figma 12036:48452) under its field -->
  <ng-template #saveError let-row>
    <div class="message message--error" role="alert" data-testid="drawing-set-page-save-error">
      <mat-icon>error_outline</mat-icon>
      <span class="message-text">{{ data.host.saveStatus(row)?.text }}
        <!-- Review I4: a way out; the page goes back to what is saved -->
        <!-- Review N1: no mousedown focus change, so the field does not blur and save what is being discarded -->
        <button type="button" class="link-button" data-testid="drawing-set-page-discard" (mousedown)="$event.preventDefault()" (click)="discardChange(row)">Discard change</button>
      </span>
    </div>
  </ng-template>
  <!-- Keyed on the page (C1): each page gets fresh inputs, so text typed on one page never shows, or saves, on the next -->
  @for (row of shownRows; track row.uuid) {
    <!-- Review I4: "Discard change" bumps the key, so the page's fields are drawn again from the saved row -->
    @for (key of resetKeys; track key) {
    <div class="heading-row">
      <div class="heading-text">
        @if (issue; as at) {
          <!-- W46: where this page sits among the pages to fix -->
          <div class="issue-text" data-testid="drawing-set-page-issue">Issue {{ at.index + 1 }} of {{ at.total }}</div>
        }
        <div class="heading text-type-2" tabindex="-1" data-testid="drawing-set-page-heading">Page {{ row.page_number }}</div>
        @if (!data.host.filteredToProblems) {
          <p class="sub mat-body-1" data-testid="drawing-set-page-position">{{ index + 1 }} of {{ rows.length }}@if (row.number) { · {{ row.number }} }</p>
        } @else if (row.number) {
          <p class="sub mat-body-1" data-testid="drawing-set-page-position">{{ row.number }}</p>
        }
      </div>
      @if (editable) {
        <button mat-icon-button type="button" class="more-button" [matMenuTriggerFor]="pageMenu" aria-label="More actions" data-testid="drawing-set-page-more">
          <mat-icon>more_horiz</mat-icon>
        </button>
        <mat-menu #pageMenu="matMenu" class="tab-more-menu">
          <button mat-menu-item type="button" data-testid="drawing-set-page-drop" (click)="drop(row)">Drop page</button>
        </mat-menu>
      }
    </div>

    <button type="button" class="preview" data-testid="drawing-set-page-preview" [attr.aria-label]="'Open page ' + row.page_number + ' full size'"
      (click)="data.host.openViewer(row)">
      @if (row.preview_url || row.thumb_url) {
        <img [src]="row.preview_url || row.thumb_url" [alt]="'Page ' + row.page_number" data-testid="drawing-set-page-image" />
        <span class="preview-hint mat-caption"><mat-icon>zoom_in</mat-icon>Open full size</span>
      } @else {
        <span class="preview-empty mat-body-1">The page preview shows here once the page is inspected.</span>
      }
    </button>
    @if (row.title_block_url) {
      <!-- R56b: the stored title column, whole -->
      <div class="title-block">
        <span class="field-label">Title block</span>
        <img [src]="row.title_block_url" [alt]="'Title block of page ' + row.page_number" loading="lazy" data-testid="drawing-set-page-title-block" />
      </div>
    }

    <div class="fields" (focusin)="data.host.editingStarted(row)" (focusout)="data.host.editingEnded(row)">
      <label class="field">
        <span class="field-label">Sheet number</span>
        <input #numberInput class="field-input" [class.field-input--error]="data.host.saveFailed(row, 'number')" maxlength="64"
          data-testid="drawing-set-page-number" [value]="row.number ?? ''" [disabled]="!editable"
          (blur)="data.host.commit(row, 'number', numberInput)" (keydown.enter)="enterNumber(row, numberInput)" />
        @if (data.host.duplicateNumber(row)) { <span class="field-hint mat-caption" data-testid="drawing-set-page-number-hint">{{ row.number }} appears twice in this set</span> }
        @if (!row.number?.trim() && !row.skip_publish) { <span class="field-hint mat-caption" data-testid="drawing-set-page-number-hint">Enter a sheet number to publish.</span> }
        @if (data.host.saveFailed(row, 'number')) { <ng-container [ngTemplateOutlet]="saveError" [ngTemplateOutletContext]="{ $implicit: row }" /> }
      </label>
      <!-- W46: a cover, index or blank page can stay out of the publish -->
      <mat-checkbox #skipBox color="primary" class="skip-check" data-testid="drawing-set-page-skip" [checked]="!!row.skip_publish" [disabled]="!editable"
        (change)="data.host.setSkip(row, $event.checked)">Skip this page</mat-checkbox>
      @if (data.host.saveFailed(row, 'skip_publish')) { <ng-container [ngTemplateOutlet]="saveError" [ngTemplateOutletContext]="{ $implicit: row }" /> }
      <label class="field">
        <span class="field-label">Title</span>
        <input #titleInput class="field-input" [class.field-input--error]="data.host.saveFailed(row, 'title')" maxlength="256" data-testid="drawing-set-page-title" [value]="row.title ?? ''" [disabled]="!editable"
          (blur)="data.host.commit(row, 'title', titleInput)" (keydown.enter)="enterField(titleInput)" />
        @if (data.host.saveFailed(row, 'title')) { <ng-container [ngTemplateOutlet]="saveError" [ngTemplateOutletContext]="{ $implicit: row }" /> }
      </label>
      <div class="field-row">
        <label class="field">
          <span class="field-label">Discipline</span>
          <input #disciplineInput class="field-input" [class.field-input--error]="data.host.saveFailed(row, 'discipline')" maxlength="64" placeholder="Civil" data-testid="drawing-set-page-discipline" [value]="row.discipline ?? ''"
            [disabled]="!editable" (blur)="data.host.commit(row, 'discipline', disciplineInput)" (keydown.enter)="enterField(disciplineInput)" />
          @if (data.host.saveFailed(row, 'discipline')) { <ng-container [ngTemplateOutlet]="saveError" [ngTemplateOutletContext]="{ $implicit: row }" /> }
        </label>
        <label class="field">
          <span class="field-label">Type</span>
          <select class="field-input" [class.field-input--error]="data.host.saveFailed(row, 'sheet_type')" data-testid="drawing-set-page-type" [disabled]="!editable" (change)="data.host.commitType(row, $any($event.target).value)">
            @for (t of sheetTypes; track t.value) {
              <option [value]="t.value" [selected]="t.value === (row.sheet_type ?? 'other')">{{ t.label }}</option>
            }
          </select>
          @if (data.host.saveFailed(row, 'sheet_type')) { <ng-container [ngTemplateOutlet]="saveError" [ngTemplateOutletContext]="{ $implicit: row }" /> }
        </label>
      </div>
      <div class="field">
        <span class="field-label">Station</span>
        <div class="station-range">
          <input #stationStart class="field-input" [class.field-input--error]="data.host.saveFailed(row, 'station_start')" maxlength="16" data-testid="drawing-set-page-station-start" aria-label="Station from"
            [placeholder]="data.host.stationPlaceholder(row, 'station_start')" [class.field-input--hint]="data.host.stationIsSuggestion(row, 'station_start')"
            [attr.aria-description]="data.host.stationHintNote(row, 'station_start')" [value]="stationText(row.station_start)" [disabled]="!editable"
            (blur)="data.host.commitStation(row, 'station_start', stationStart)" (keydown.enter)="enterField(stationStart)" />
          <span class="station-to mat-body-1" aria-hidden="true">to</span>
          <input #stationEnd class="field-input" [class.field-input--error]="data.host.saveFailed(row, 'station_end')" maxlength="16" data-testid="drawing-set-page-station-end" aria-label="Station to"
            [placeholder]="data.host.stationPlaceholder(row, 'station_end')" [class.field-input--hint]="data.host.stationIsSuggestion(row, 'station_end')"
            [attr.aria-description]="data.host.stationHintNote(row, 'station_end')" [value]="stationText(row.station_end)" [disabled]="!editable"
            (blur)="data.host.commitStation(row, 'station_end', stationEnd)" (keydown.enter)="enterField(stationEnd)" />
        </div>
        @if (data.host.saveFailed(row, 'station_start')) { <ng-container [ngTemplateOutlet]="saveError" [ngTemplateOutletContext]="{ $implicit: row }" /> }
      </div>
      <div class="field-row">
        <label class="field">
          <span class="field-label">Revision</span>
          <input #labelInput class="field-input" [class.field-input--error]="data.host.saveFailed(row, 'label')" maxlength="32" data-testid="drawing-set-page-label" [placeholder]="data.host.labelPlaceholder(row)" [value]="row.label ?? ''"
            [disabled]="!editable" (blur)="data.host.commit(row, 'label', labelInput)" (keydown.enter)="enterField(labelInput)" />
          @if (data.host.saveFailed(row, 'label')) { <ng-container [ngTemplateOutlet]="saveError" [ngTemplateOutletContext]="{ $implicit: row }" /> }
        </label>
        <div class="field">
          <span class="field-label">Match</span>
          @if (row.match.kind === 'ambiguous' && editable) {
            <select class="field-input" data-testid="drawing-set-page-match" aria-label="Pick the sheet" (change)="data.host.pickCandidate(row, $any($event.target).value || null)">
              <option value="" disabled selected>Pick a sheet</option>
              @for (c of row.match.candidates; track c.sheetUuid) { <option [value]="c.sheetUuid">{{ c.number }}</option> }
              <option value="">New sheet</option>
            </select>
          } @else {
            <span class="match">
              @if (row.match.kind === 'ambiguous') {
                <!-- W47 / review M1: a sheet still to pick is a warning, as in the list -->
                <span class="status-badge status-badge--warning" data-testid="drawing-set-page-match-badge">{{ data.host.matchLabel(row) }}</span>
              } @else {
                <span class="chip" [class.chip--replaces]="row.match.kind === 'replaces'">{{ data.host.matchLabel(row) }}</span>
              }
            </span>
          }
          @if (data.host.saveFailed(row, 'sheetUuid')) { <ng-container [ngTemplateOutlet]="saveError" [ngTemplateOutletContext]="{ $implicit: row }" /> }
        </div>
      </div>
      @if (row.inspect_warning) { <p class="field-warning mat-body-1"><mat-icon>warning_amber</mat-icon>{{ row.inspect_warning }}</p> }
    </div>

    }
  } @empty {
    <p class="sub mat-body-1">This page is no longer in the list.</p>
  }
  @if (row) {
    <!-- Outside the keyed block, so Previous / Next keep focus from page to page -->
    <!-- W46 item 7: what happened to the change just made -->
    <p class="save-line" role="status" aria-live="polite" data-testid="drawing-set-page-save" [class.save-line--saved]="status?.status === 'saved'">
      @if (status; as st) {
        @if (st.status === 'saved') { <mat-icon class="save-icon">check</mat-icon> }
        <span>{{ st.text }}</span>
      }
    </p>
    <div class="panel-footer">
      <app-secondary-button buttonId="drawing-set-page-prev" data-testid="drawing-set-page-prev" [isDisabled]="!canPrev || moving" (handleClick)="prev()">Previous</app-secondary-button>
      <app-primary-button buttonId="drawing-set-page-next" data-testid="drawing-set-page-next" [isDisabled]="!canNext || moving" (handleClick)="next()">Next</app-primary-button>
    </div>
    <p class="keys mat-caption">PageUp / PageDown move between pages</p>
  }
</div>
